import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  MessageSquare, 
  LayoutTemplate, 
  Settings, 
  Wrench, 
  Home, 
  Clock, 
  CheckCircle2, 
  Sparkles
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

export interface StepDetail {
  number: string;
  title: string;
  tagline: string;
  desc: string;
  deliverables: string[];
  duration: string;
  icon: any;
}

export const steps: StepDetail[] = [
  {
    number: "01",
    title: "Discovery Consultation",
    tagline: "Vision & Site Analysis",
    desc: "We meet on-site or at our studio to understand your lifestyle requirements, aesthetic tastes, and budget expectations. We perform comprehensive 3D digital measurements of your space.",
    deliverables: ["Comprehensive site measurements", "Initial space allocation layout", "Budget estimation & timeline draft"],
    duration: "1 - 3 Days",
    icon: MessageSquare
  },
  {
    number: "02",
    title: "Bespoke 3D Designing",
    tagline: "Hyper-Realistic Renders",
    desc: "Our design architects create detailed 2D layouts and gorgeous, photo-realistic 3D designs. You get a virtual walkthrough of every room to visualize materials, colors, and lighting before production.",
    deliverables: ["2D architectural floorplans", "Photorealistic 3D room renders", "Material & finish selection board"],
    duration: "10 - 15 Days",
    icon: LayoutTemplate
  },
  {
    number: "03",
    title: "Factory Manufacturing",
    tagline: "German Precision Fabrication",
    desc: "Once design is approved, your modular furniture is fabricated at our state-of-the-art facility using German CNC machines. This ensures flawless finishes, premium edge-banding, and exact dimensions.",
    deliverables: ["German-precision cabinetry", "Rigid quality control checks", "Hardware pre-drilling & calibration"],
    duration: "20 - 25 Days",
    icon: Settings
  },
  {
    number: "04",
    title: "On-Site Installation",
    tagline: "Expert Craftsmanship",
    desc: "Our trained technicians and supervisors manage civil work, painting, false ceilings, electrical adjustments, and modular assembly on-site. We keep you updated daily through a dedicated project manager.",
    deliverables: ["Civil, electrical & false ceiling work", "Cabinet installation & fitting", "Lighting & hardware setup"],
    duration: "15 - 20 Days",
    icon: Wrench
  },
  {
    number: "05",
    title: "Snag-Free Handover",
    tagline: "Move-In Ready Clean",
    desc: "We conduct a rigorous 120-point quality audit to calibrate every hinge and alignment. After deep industrial cleaning, we hand over the keys to your dream home, backed by a 10-year warranty.",
    deliverables: ["120-point rigorous quality audit", "Professional deep cleaning", "Warranty handbook & keys handover"],
    duration: "3 - 5 Days",
    icon: Home
  }
];

export function HowWeWorkSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHovering = useRef(false);

  // Auto-play interval triggers step rotation every 2.5 seconds unless paused
  useEffect(() => {
    if (isPaused) {
      if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
      return;
    }

    autoPlayTimerRef.current = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);

    return () => {
      if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
    };
  }, [isPaused]);

  // Clean up click timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    };
  }, []);

  const activeStepData = steps[activeStep];

  const handleMouseEnter = () => {
    isHovering.current = true;
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    isHovering.current = false;
    // Only resume autoplay if we aren't waiting on a manual click timeout
    if (!clickTimeoutRef.current) {
      setIsPaused(false);
    }
  };

  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setIsPaused(true);

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // Set an 8-second window of manual pause, then check if we should resume
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
      // Only resume if the user has moved their cursor off the section
      if (!isHovering.current) {
        setIsPaused(false);
      }
    }, 8000);
  };

  return (
    <section 
      id="process" 
      aria-label="Our interior design process" 
      className={`py-20 sm:py-28 transition-colors duration-500 relative overflow-hidden ${
        isDark ? 'bg-primary text-white' : 'bg-slate-50 text-slate-900'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Dynamic Background Blurs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 mb-4"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent">OUR EXECUTION ENGINE</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-3xl sm:text-4xl lg:text-[42px] font-poppins font-bold leading-tight mb-4 tracking-tight ${
              isDark ? 'text-white' : 'text-slate-950'
            }`}
          >
            Our Work Process — <span className="text-accent">Zero Friction. Flawless Handover.</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-base sm:text-lg max-w-xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-slate-500'
            }`}
          >
            We've engineered a standard execution framework to eliminate delays, cost overruns, and quality compromises.
          </motion.p>
        </div>

        {/* Pipeline Track Navigation */}
        <div className="mb-16 md:mb-24 px-4 sm:px-12">
          <div className="relative">
            {/* Timeline Bar Background Line */}
            <div className={`absolute top-7 left-[10%] right-[10%] h-[2px] z-0 pointer-events-none ${
              isDark ? 'bg-white/10' : 'bg-slate-200'
            }`} />
            
            {/* Animated Glowing Color Bar (Smooth transition via Framer Motion) */}
            <motion.div 
              className="absolute top-7 left-[10%] h-[2px] bg-gradient-to-r from-accent to-[#E0B880] z-0 pointer-events-none"
              initial={{ width: '0%' }}
              animate={{ width: `${activeStep * 20}%` }}
              transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
            />

            {/* Interactive Steps Grid */}
            <div className="relative z-10 grid grid-cols-5 gap-2 select-none">
              {steps.map((step, index) => {
                const isActive = activeStep === index;
                const isCompleted = index < activeStep;
                const Icon = step.icon;

                return (
                  <div key={index} className="flex flex-col items-center group cursor-pointer" onClick={() => handleStepClick(index)}>
                    {/* Step Bubble Ring (Solid backgrounds to completely cover the line behind them) */}
                    <div
                      className={`w-14 h-14 rounded-full border-2 flex items-center justify-center relative z-10 transition-all duration-500 ${
                        isActive 
                          ? isDark
                            ? 'border-accent bg-[#0F172A] text-accent scale-110 shadow-[0_0_25px_rgba(201,162,107,0.35)]'
                            : 'border-accent bg-slate-50 text-accent scale-110 shadow-[0_0_25px_rgba(201,162,107,0.2)]'
                          : isCompleted
                            ? 'border-accent bg-accent text-white shadow-[0_0_15px_rgba(201,162,107,0.25)]'
                            : isDark
                              ? 'border-white/10 bg-[#0F172A] text-gray-550 hover:border-white/30 hover:text-white'
                              : 'border-slate-300 bg-slate-50 text-slate-400 hover:border-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      
                      {/* Floating Counter Bubble */}
                      <div
                        className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center font-bold text-[9px] shadow-sm transition-all duration-300 ${
                          isActive 
                            ? 'bg-accent text-white scale-100' 
                            : isDark ? 'bg-gray-700 text-gray-300 scale-90' : 'bg-slate-200 text-slate-650 scale-90'
                        }`}
                      >
                        {step.number}
                      </div>
                    </div>
                    
                    {/* Step Brief Tagline (Desktop Only) */}
                    <div className="hidden md:block text-center mt-3 max-w-[120px] transition-all duration-300">
                      <div className={`font-poppins text-xs font-bold ${
                        isActive ? 'text-accent' : isDark ? 'text-gray-400 hover:text-white' : 'text-slate-655 hover:text-slate-900'
                      }`}>
                        {step.title}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detailed Glassmorphic Step Pane (Static wrapper prevents height jumps and exit glitches) */}
        <div className="max-w-5xl mx-auto">
          <div
            className={`rounded-3xl p-6 sm:p-10 border relative overflow-hidden transition-all duration-300 ${
              isDark 
                ? 'bg-[#111827]/80 border-white/[0.06] shadow-2xl' 
                : 'bg-white border-slate-200/60 shadow-xl shadow-slate-100/50'
            }`}
            style={{ minHeight: '340px' }}
          >
            {/* Background ambient badge glow */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="grid md:grid-cols-12 gap-8 items-stretch"
              >
                {/* Left Pane of detail: Title & Metas */}
                <div className="md:col-span-5 flex flex-col justify-between border-b md:border-b-0 md:border-r pb-6 md:pb-0 md:pr-8 border-slate-200/20">
                  <div>
                    <div className="flex items-center gap-3.5 mb-4">
                      <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold px-3 py-1 bg-accent/15 border border-accent/25 rounded-md text-accent">
                        STAGE {activeStepData.number} / 05
                      </span>
                    </div>

                    <h3 className={`text-2xl sm:text-3xl font-poppins font-black leading-tight tracking-tight mb-2 ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}>
                      {activeStepData.title}
                    </h3>
                    
                    <div className="text-accent font-poppins text-xs uppercase font-extrabold tracking-wider mb-6 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-accent animate-pulse" />
                      {activeStepData.tagline}
                    </div>
                  </div>

                  {/* Clock Timeline estimate */}
                  <div className={`inline-flex items-center gap-2.5 px-4.5 py-3 rounded-2xl border w-fit ${
                    isDark ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-slate-50 border-slate-150 text-slate-700 shadow-sm'
                  }`}>
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <div className="text-[8px] uppercase font-extrabold tracking-wider opacity-60 font-poppins leading-tight">ESTIMATED DURATION</div>
                      <div className="text-[13px] font-black font-poppins text-accent">{activeStepData.duration}</div>
                    </div>
                  </div>
                </div>

                {/* Right Pane of detail: Description & Deliverables */}
                <div className="md:col-span-7 flex flex-col justify-center">
                  <p className={`text-sm leading-relaxed mb-6 font-medium ${
                    isDark ? 'text-gray-350' : 'text-slate-655'
                  }`}>
                    {activeStepData.desc}
                  </p>

                  <div>
                    <h4 className={`text-[10px] uppercase font-poppins tracking-wider font-extrabold mb-3.5 ${
                      isDark ? 'text-gray-550' : 'text-slate-450'
                    }`}>
                      KEY MILESTONES & DELIVERABLES
                    </h4>
                    <ul className="space-y-3">
                      {activeStepData.deliverables.map((del, index) => (
                        <motion.li 
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          key={del} 
                          className="flex items-start gap-3 text-xs sm:text-[13px] font-semibold leading-snug"
                        >
                          <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span className={isDark ? 'text-gray-200' : 'text-slate-700'}>{del}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

              </motion.div>
            </AnimatePresence>
          </div>
        </div>

      </div>
    </section>
  );
}
